import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addYears } from 'date-fns/addYears';
import { differenceInDays } from 'date-fns/differenceInDays';
import { eachMonthOfInterval } from 'date-fns/eachMonthOfInterval';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfYear } from 'date-fns/endOfYear';
import { format } from 'date-fns/format';
import { getDay } from 'date-fns/getDay';
import { getDefaultOptions } from 'date-fns/getDefaultOptions';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isValid } from 'date-fns/isValid';
import type { Locale } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { parse } from 'date-fns/parse';
import { setDay } from 'date-fns/setDay';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';
import { setYear } from 'date-fns/setYear';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfYear } from 'date-fns/startOfYear';
import { subMonths } from 'date-fns/subMonths';
import { subYears } from 'date-fns/subYears';
import { toDate } from 'date-fns/toDate';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, queryAll } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksCalendarChangeEvent } from '../../events/blocks-calendar-change';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../icon/icons/arrow-triangular-left';
import '../icon/icons/arrow-triangular-right';
import '../icon/icons/chevron-down';
import type { DayDetails, YearDetails } from './calendar.model';
import styles from './calendar.styles';
import { TZDate, getConvertedDate } from './date-utils';

/**
 * @slot before - The component before content
 * @slot after - The component after content
 * @fires bl-calendar-change - emitted when a day is selected
 * @csspart base - The component's base wrapper.
 * @csspart header - The component's header wrapper.
 * @csspart previous-nav - The component's previous-nav button.
 * @csspart part-before - The component's before slot.
 * @csspart triangular-left-icon - The component's triangular-left icon.
 * @csspart month-button - The component's month-button.
 * @csspart chevron-down-icon - The component's chevron-down icon.
 * @csspart next-nav - The component's next-nav button.
 * @csspart part-after - The component's after slot.
 * @csspart triangular-right-icon - The component's triangular-right icon.
 * @csspart table-container - The component's table-container wrapper.
 * @csspart table - The component's table wrapper.
 * @csspart table-header - The component's table-header wrapper.
 * @csspart week-table-row - The component's week-table-row wrapper.
 * @csspart header-cell - The component's header-cell.
 * @csspart table-body - The component's table-body wrapper.
 * @csspart day-table-row - The component's day-table-row.
 * @csspart table-cell - The component's table-cell.
 * @csspart day - The component's day element.
 * @csspart month-popup - The component's month-popup wrapper.
 * @csspart month-selector - The component's month-selector wrapper.
 * @csspart month-list - The component's month-list wrapper.
 * @csspart month-selector_item - The component's month-selector_item.
 * @csspart month-selector-year - The component's month-selector-year.
 * @csspart month-selector_sub-list - The component's month-selector_sub-list wrapper.
 * @csspart month-selector_sub-item - The component's month-selector_sub-item wrapper.
 * @csspart month-selector_button - The component's month-selector_button wrapper.
 * @csspart abbr - The component's abbr element.
 */

export class BlocksCalendar extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * locale object from date-fns/locale
   */
  @property({ type: Object })
  locale: Locale = enUS;

  /**
   * Current Month/Year
   */
  @property({ type: Object })
  currentDate: Date | TZDate = new Date();

  /**
   * Current Month/Year display in header
   */
  @property({ type: Object })
  navDate: Date | undefined;

  /**
   * Minimum date for disabled dates (format: yyyy/mm/dd)
   */
  @property({ type: String })
  disabledMinDate: string | null | undefined;

  /**
   * Maximum date for disabled dates (format: yyyy/mm/dd)
   */
  @property({ type: String })
  disabledMaxDate: string | null | undefined;

  /**
   * Array of specific dates to be disabled
   */
  @property({ type: Array })
  disabledDates: string[] | undefined;

  /**
   * Array of week days to be disabled
   * Format: '0' for Sunday, '1' for Monday, '2' for Tuesday, '3' for Wednesday, '4' for Thursday, '5' for Friday, '6' for Saturday
   * Example: ['0', '6'] to disable Sundays and Saturdays
   **/
  @property({ type: Array })
  disabledWeekDays: string[] | undefined;

  /**
   * Start date for disabling a range of dates
   */
  @property({ type: String })
  disabledRangeStartDate: string | undefined;

  /**
   * End date for disabling a range of dates
   */
  @property({ type: String })
  disabledRangeEndDate: string | undefined;

  /**
   * Amount of years to go before and after the current date
   */
  @property({ type: Number })
  multiYear = 3;

  @property({ type: String })
  previousButtonText = 'Previous Month';

  @property({ type: String })
  nextButtonText = 'Next Month';

  @property({ type: String })
  arialabel = 'Month Calendar';

  /**
   * Container for current days in month
   */
  @property({ type: Array })
  days: Array<Array<DayDetails>> = [];

  /**
   * Container for years toggle
   */
  @property({ type: Array })
  years: YearDetails[] = [];

  /**
   * Value to track month picker display
   */
  @property({ type: Boolean })
  showMonthPopup: boolean | undefined;

  /**
   * Make active calendar date inactive
   */
  @property({ type: Boolean })
  resetDates = false;

  /**
   * Selected date
   */
  @property({ type: String })
  selectedDate: string | Date | null | undefined;

  /**
   * Set a specific date to be active
   */
  @property({ type: String })
  setActiveDate: string | null | undefined;

  /**
   * Internal ID only for labeling datepicker popup
   */
  @property({ type: String })
  datepickerId: string | undefined;

  /**
   * Specify date format for UI display
   */
  @property({ type: String })
  dateFormat = 'MMM dd, yyyy';

  /**
   * Specify "full" date format for aria role
   */
  @property({ type: String })
  fullDateFormat = 'd, EEEE MMMM yyyy';

  /**
   * Disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Field note icon name
   */
  @property({ type: String })
  iconName: string | undefined;

  /**
   * The `timeZone` property represents the time zone that will be passed to the `TZDate` object.
   * This property accepts a string that can either be:
   * - **An IANA time zone identifier** (e.g., `'America/New_York'`, `'Europe/London'`, `'Asia/Kolkata'`).
   * - **A UTC offset string** (e.g., `'-0800'` for UTC-8 hours, or `'+0530'` for UTC+5:30 hours).
   *
   * If not provided, the default value will be `undefined`, and the system's local time zone will be used.
   **/
  @property({ type: String })
  timeZone: string | undefined;

  /**
   * Query the calendar month popup
   */
  @query('.cat-c-calendar__month-selector-popup')
  private _catCalendarMonthPopup: HTMLElement | undefined | null;

  /**
   * Query the month selector button
   */
  @query('.cat-c-calendar__month-selector-button')
  private _catCalendarMonthButton: HTMLElement | undefined;

  /*
   * Query the calender
   */
  @query('.cat-c-calendar')
  private _catCalendar: HTMLElement | undefined;

  @query('.cat-c-calendar__table-body .is-active')
  private _catIsActive: HTMLButtonElement | undefined;

  @query('.is-today:not(:disabled)')
  private _catIsToday: HTMLButtonElement | undefined;

  @queryAll('.is-available')
  private _catIsAvailableAll: NodeListOf<HTMLButtonElement> | undefined;

  @queryAll('.cat-c-calendar__item')
  private _catCalendarItems: NodeListOf<HTMLButtonElement> | undefined;

  /*
   * Currently focussed date
   */
  private focusedDate?: HTMLButtonElement | null;

  /*
   * Currently focussed position
   */
  private focusedDateIndex = 0;

  /*
   * Current focussed month position
   */
  private focusedMonthIndex = 0;

  /*
   * Selected Month
   */
  private selectedMonth?: HTMLElement;

  private monthSelectorButtonClassName = '.cat-c-month-selector__button';
  private dateSelectorIsAvailableButtonClassName = '.cat-c-calendar__item.is-available';
  private dateRowSelectorClassName = '.cat-c-calendar__table-row';
  private dateSelectorButtonClassName = '.cat-c-calendar__item';
  /*
   * focus trap
   */
  private _focusTrap?: FocusTrap;

  connectedCallback() {
    super.connectedCallback();

    this.initializeCalendarView();
  }

  /**
   * Initializes the calendar view, navigating to the correct month and setting
   * the active and/or disabled minimum date if provided.
   * 1.Set the navigation date to the start of the current month
   * 2.After the component updates, check for any active or disabled minimum dates.
   * 3.Set the active date if provided
   * 4.Set the disabled minimum date if provided
   * 5.Generate the grid for the calendar view
   */

  private initializeCalendarView() {
    /*1*/

    this.navDate = startOfMonth(this.currentDate);

    /*2*/

    this.updateComplete.then(() => {
      /*3*/

      if (this.setActiveDate) {
        const activeDate = parse(this.setActiveDate, this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });

        this.updateGrid(activeDate);
      }

      /*4*/

      if (this.disabledMinDate) {
        const activeDate = this.parseDisabledMinDate();
        this.updateGrid(activeDate);
      }
    });

    /*5*/

    this.makeGrid();
  }

  /*
   * De-activate the focus trap
   */
  disconnectedCallback() {
    this._focusTrap?.deactivate();
  }

  /**
   * Make the calendar days header
   * 1) Set the start of the week
   * 2) Set the pattern for the day of the week
   * 3) Add the day to the array
   * 4) Return the array
   */
  private weekDaysHeader() {
    const weekdays = [];
    const start = 0; /* 1 */
    const pattern = 'eee'; /* 2 */
    for (let i = 0; i < 7; i++) {
      weekdays.push(format(setDay(getConvertedDate(this.timeZone), start + i), pattern)); /* 3 */
    }
    return weekdays; /* 4 */
  }

  /**
   * Navigate previous in sequential month order
   * 1) Go back a month if able to
   * 2) Reset the calendar grid
   * 3) Reset the previous focus details when we move to next month
   */
  private subNavMonth = () => {
    if (this.canChangeSubNavMonth()) {
      this.navDate = subMonths(this.navDate ?? '', 1); /* 1 */
      this.makeGrid(); /* 2 */
      this.resetCurrentFocussedDate(); /* 3 */

      return true;
    }
  };

  /**
   * Navigate next in sequential month order
   * 1) Go forward a month if able to
   * 2) Reset the calendar grid
   * 3) Reset the previous focus details when we move to pervious month
   */
  private addNavMonth = () => {
    if (this.canChangeAddNavMonth()) {
      this.navDate = addMonths(this.navDate ?? '', 1); /* 1 */
      this.makeGrid(); /* 2 */
      this.resetCurrentFocussedDate(); /* 3 */

      return true;
    }
  };

  /**
   * Validate if month can proceed previous.
   * 1) If the current end of the month date is before the minimum date, return false
   */
  private canChangeSubNavMonth() {
    let disabledMinDate = startOfYear(subYears(this.currentDate, this.multiYear));
    if (this.disabledMinDate) {
      disabledMinDate = toDate(this.parseDisabledMinDate());
    }

    /* 1 */
    if (isBefore(subMonths(endOfMonth(this.navDate ?? ''), 1), disabledMinDate)) {
      return false;
    }

    return true;
  }

  /**
   * Validate if month can proceed next.
   * 1) If the day is after the maximum date, return false
   */
  private canChangeAddNavMonth() {
    let disabledMaxDate = endOfYear(addYears(this.currentDate, this.multiYear));
    let updatedNavDate;
    if (this.disabledMaxDate) {
      disabledMaxDate = toDate(this.parseDisabledMaxDate());
    }
    if (!this.navDate || !(this.navDate instanceof Date)) {
      return;
    }
    let navDateToString = '' + (this.navDate?.getMonth() + 1) + '/' + this.navDate?.getDate() + '/' + this.navDate?.getFullYear();
    /*****Start****
     #1175445 : this condition will check diff between disabledMaxDate & navDate
       if it is less than 31 then addMonth will not happen to avoid disabled next icon
    */
    const dayDiff = differenceInDays(this.parseDisabledMaxDate(), new Date(navDateToString));
    if (this.navDate?.getMonth() + 1 !== this.parseDisabledMaxDate()?.getMonth() + 1 && dayDiff < 31) {
      updatedNavDate = this.navDate;
    } else {
      updatedNavDate = addMonths(this.navDate, 1);
    }
    /***End*****/
    if (isAfter(updatedNavDate, disabledMaxDate)) {
      return false;
    }

    return true;
  }
  /**
   * Toggle the month popup overlay
   * 1) Starting Year: Get the current year - this.multiYear years (defaults to 3)
   * 2) Ending Year: Get the current year + this.multiYear years (defaults to 3)
   * 3) Get the years in between
   * 4) Get the months in each year
   * 5) Increment the year
   * 6) Position scroll on year of clicked month
   */
  private toggleMonthPopup = () => {
    this.showMonthPopup = !this.showMonthPopup;

    /* 1 */
    let startYear = subYears(this.currentDate, this.multiYear);

    /* 2 */
    const endYear = addYears(this.currentDate, this.multiYear);

    /* 3 */
    this.years = [];
    while (startYear <= endYear) {
      const year: YearDetails = { value: startYear };
      year.value = startYear;
      this.years.push(year);
      /* 4 */
      year.month = eachMonthOfInterval({
        start: startOfYear(startYear),
        end: endOfYear(startYear)
      });

      /* 5 */
      startYear = addYears(startYear, 1);
    }

    setTimeout(() => {
      /* 6 */
      const monthToggleYear = this._catCalendarMonthButton?.dataset.year;
      this.shadowRoot?.querySelector('#cat-year-' + monthToggleYear)?.scrollIntoView({ block: 'nearest' });
    }, 1);
  };

  /*
   * Check whether the element is visible in the DOM
   * This is required for the date picker as it is visible only on selection, else this will try to activate an hidden element
   */
  private isVisible(elem: HTMLElement) {
    if (!elem) {
      return;
    }
    return elem.offsetWidth > 0 || elem.offsetHeight > 0 || elem.getClientRects().length > 0;
  }

  /*
   *  Activate the focus trap
   */
  firstUpdated() {
    const calendarPanel = this._catCalendar;
    if (calendarPanel && this.isVisible(calendarPanel)) {
      this._focusTrap = createFocusTrap(calendarPanel, {
        initialFocus: false,
        tabbableOptions: { getShadowRoot: true },
        fallbackFocus: this._catCalendar,
        allowOutsideClick: true
      });
      /**To activate focus trap */
      this._focusTrap?.activate();
    } else {
      this._focusTrap?.deactivate();
    }
    this.currentDate = getConvertedDate(this.timeZone);
  }

  /**
   * Set tabindex and focus state on month popup current month
   * 1) If an active month has been selected, put the focus there
   * 2) If no active month has been selected, put the focus on the current month
   */
  async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    if (_changedProperties.has('locale')) {
      await this.updateComplete;
      const locale = this.locale;
      setDefaultOptions({ locale });
      this.makeGrid();
    }
    if (_changedProperties.has('timeZone') && this.timeZone) {
      this.currentDate = getConvertedDate(this.timeZone);
      this.initializeCalendarView();
    }

    if (_changedProperties.has('showMonthPopup')) {
      await this.updateComplete;
      const currentMonth: HTMLElement | undefined | null = this._catCalendarMonthPopup?.querySelector('.is-current');
      let activeMonth: HTMLElement | undefined | null = this._catCalendarMonthPopup?.querySelector('.is-active');

      if (activeMonth && this.showMonthPopup && (activeMonth as HTMLButtonElement)?.disabled) {
        activeMonth = this.checkFirstEnabledMonth(activeMonth);
      }
      if (activeMonth) {
        /* 1 */
        activeMonth.tabIndex = -1;
        if (this.showMonthPopup === true) {
          activeMonth.focus();
          this.selectedMonth = activeMonth;
          this.setFocussedMonthIndex();
        }
      } else if (currentMonth) {
        /* 2 */
        currentMonth.tabIndex = -1;
        if (this.showMonthPopup === true) {
          currentMonth.focus();
          this.selectedMonth = currentMonth;
          this.setFocussedMonthIndex();
        }
      }
    }
    if (_changedProperties.has('setActiveDate')) {
      await this.updateComplete;
      let activeDate = null;
      if (this.setActiveDate) {
        activeDate = parse(this.setActiveDate, this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });
      } else {
        this.resetCurrentFocussedDate();
      }

      this.selectedDate = activeDate;
      this.updateGrid(activeDate || this.currentDate);
    }
  }

  /**Focus on the first enabled button in a sub-list or the selected month if enabled
   * 1.If disabled, find all sub-items within the parent list and return the first enabled button.
   **/
  private checkFirstEnabledMonth(selectedDate: HTMLElement) {
    const monthList = Array.from(
      selectedDate?.closest('.cat-c-month-selector__list')?.querySelectorAll('.cat-c-month-selector__sub-item') || []
    ); /*1*/
    const firstEnabledMonth = monthList
      .map((month) => month.querySelector<HTMLButtonElement>('.cat-c-month-selector__button'))
      .find((button) => button && !button.disabled); /*1*/

    return firstEnabledMonth;
  }
  /**
   * Handle keydown
   * 1) Close the month calendar popup when escape is hit when the user is focused within the popup
   * 2) Return focus back to the month selector button
   * 3) Reset the previous focus details
   * 4) If arrow right key pressed then focus to next month
   * 5) If arrow down key pressed then focus to below month
   * 6) If arrow left or up key pressed then focus to previous month
   * 7) If arrow up key pressed then focus to previous row month
   * 8)Focus on the first enabled button in a sub-list or the selected month if enabled
   *  a) Check if the selected month button is disabled.
   *  b) Focus on the first enabled button, or if the selected month is enabled, focus on it directly.
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === KEYS.TAB && this.selectedMonth) {
      if ((this.selectedMonth as HTMLButtonElement).disabled) {
        /*8-a*/
        const firstEnabledMonth = this.checkFirstEnabledMonth(this.selectedMonth);
        this.focusOnMonth(firstEnabledMonth as HTMLElement); /*8-b*/
      } else {
        this.focusOnMonth(this.selectedMonth);
      }
      e.preventDefault();
      return;
    }

    if (e.key === KEYS.ESCAPE && this._catCalendarMonthButton) {
      this.showMonthPopup = false; /* 1 */
      this._catCalendarMonthButton.tabIndex = 0; /* 2 */
      this._catCalendarMonthButton.focus();
    }

    this.resetCurrentFocussedDate(); /* 3 */

    /* 4 */
    if (e.key === KEYS.RIGHT_ARROW) {
      this.getNextMonth();
    }
    /* 5 */
    if (e.key === KEYS.DOWN_ARROW) {
      this.getBelowMonth();
    }

    /* 6 */
    if (e.key === KEYS.LEFT_ARROW) {
      this.getPreviousMonth();
    }
    /* 7 */
    if (e.key === KEYS.UP_ARROW) {
      this.getUpperMonth();
    }
  }

  /**
   * Update the calendar grid to the month and year selected
   * @param date date timestamp
   */
  public updateGrid = (date: Date) => {
    if (date !== undefined) {
      this.resetCurrentFocussedDate();
      const yearNumber = getYear(date);
      this.navDate = setYear(date, yearNumber);

      if (this.showMonthPopup === true) {
        this.toggleMonthPopup();
      }
      this.makeGrid();
    }
  };

  // Focus back on the month header button after month grid has been updated
  private focusMonthButton() {
    const monthButton = this.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-calendar__month-selector-button');
    if (monthButton) {
      monthButton.focus();
    }
  }

  /**
   * Create days of month grid
   * 1) if we have an active date available the focus on that after build the grid so that next key focus starts from this
   */
  private makeGrid = () => {
    setTimeout(() => {
      this.days = [];
      const weekStart = 0;
      const selectedMonth = getMonth(this.navDate ?? '');
      let currentDate = startOfMonth(this.navDate ?? '');
      let firstDayOfMonth = (getDay(currentDate) + 7 - weekStart) % 7;
      let weekDays: DayDetails[] = [];

      // Add empty days to align the first day of the month with the correct day of the week
      while (firstDayOfMonth > 0) {
        weekDays.push({ value: null, available: false });
        firstDayOfMonth--;
      }

      // Add the days of the selected month
      while (getMonth(currentDate) === selectedMonth) {
        const day = { value: currentDate, available: true };
        weekDays.push(day);
        currentDate = addDays(currentDate, 1);
      }

      // Add empty days to align the end of the month with the end of the week
      while (weekDays.length % 7 !== 0) {
        weekDays.push({ value: null, available: false });
      }

      let counter = 0;
      let i = 0;
      let week: DayDetails[] = [];

      do {
        week.push(weekDays[i]);
        i++;
        counter++;

        if (counter === 7 || i === weekDays.length) {
          this.days.push(week);
          week = [];
          counter = 0;
        }
      } while (i < weekDays.length);
    }, 1);

    /* 1 */
    this.focusOnActive();
  };

  /**
   * Focus on active date
   */
  private focusOnActive() {
    setTimeout(() => {
      if (this._catIsActive) {
        this.setFocusOnDate(this._catIsActive);
      }
    }, 3);
  }

  /**
   * @fire bl-calendar-change
   * Change output binding
   */
  private onChanged = () => {
    this.dispatch({
      eventName: 'bl-calendar-change',
      detailObj: {
        value: format(this.selectedDate ?? '', this.dateFormat, { locale: enUS }),
        rawDate: this.selectedDate
      }
    }) as BlocksCalendarChangeEvent;

    return format(this.selectedDate ?? '', this.dateFormat);
  };

  /**
   * Select the day and emit
   * 1) Bind to form control
   * @param day selected day
   */
  private selectDay = (day: DayDetails) => {
    // Clear date reset
    this.resetDates = false;

    if (day.available) {
      this.selectedDate = day.value;
      this.onChanged();
      this.focusOnActive();
    }
  };

  /**
   * Apply active state if day has been selected
   * @param day selected day
   */
  private isActive(day: DayDetails) {
    const parseWithEnglishLocale = parse(this.setActiveDate || '', this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });
    const parseWithCurrentLocale = parse(this.setActiveDate || '', this.dateFormat, getConvertedDate(this.timeZone), {
      locale: (getDefaultOptions() as { locale: Locale }).locale
    });
    const dateObject = isValid(parseWithEnglishLocale) ? parseWithEnglishLocale : parseWithCurrentLocale;
    const activeDateString = dateObject;
    const activeDate = startOfDay(toDate(activeDateString));
    const currentDateInLoop = startOfDay(day.value ?? '');

    /**
     * If a specific date is set, highlight it
     */
    if (day.available && this.selectedDate && this.resetDates === false) {
      const selectedDateString = format(this.selectedDate, 'yyyy-MM-dd');
      const dayValueDateString = format(day.value ?? '', 'yyyy-MM-dd');
      return isEqual(selectedDateString, dayValueDateString);
    } else if (activeDate && isEqual(currentDateInLoop, activeDate)) {
      return true;
    }
  }

  /**
   * Check if day can be selected
   * 1) Check if the day is before the minimum date or after the maximum date
   * 2) Check if the day is in the disabled dates array
   * 3) Check if the day is in the disabled week days array
   * 4) Check if the day is in the disabled range of dates
   * 5) If none of the above, the day is available
   * @param day selected day
   */
  private isAvailableDay(day: Date): boolean {
    /* 1 */
    let disabledMinDate = startOfYear(subYears(this.currentDate, this.multiYear));
    if (this.disabledMinDate) {
      const parsedDate = this.parseDisabledMinDate();
      disabledMinDate = toDate(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()));
      disabledMinDate.setUTCHours(0, 0, 0, 0);
    }

    let disabledMaxDate = endOfYear(addYears(this.currentDate, this.multiYear));
    if (this.disabledMaxDate) {
      const parsedDate = this.parseDisabledMaxDate();
      disabledMaxDate = toDate(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()));
      disabledMaxDate.setUTCHours(0, 0, 0, 0);
    }

    day = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));

    if (isAfter(disabledMinDate, day) || isBefore(disabledMaxDate, day)) {
      return false;
    }

    /* 2 */
    if (this.checkIfDateDisabled(day)) {
      return false;
    }

    /* 3 */
    if (this.disabledWeekDays?.includes(day.getDay().toString())) {
      return false;
    }

    /* 4 */
    if (this.disabledRangeStartDate && this.disabledRangeEndDate) {
      const { rangeStartDate, rangeEndDate } = this.getDateRange();
      if (isAfter(day, rangeStartDate) && isBefore(day, rangeEndDate)) {
        return false;
      }
    }

    /* 5*/
    return true;
  }

  private getDateRange() {
    const rangeStartDate = parse(this.disabledRangeStartDate ?? '', this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });
    const rangeEndDate = addDays(parse(this.disabledRangeEndDate ?? '', this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS }), 1);
    return { rangeStartDate, rangeEndDate };
  }

  /** Parse the disabled minimum date string to a Date object
   * @returns Date object representing the disabled minimum date
   */
  private checkIfDateDisabled(day: Date) {
    return this.disabledDates?.some((dateString) =>
      isSameDay(parse(dateString, this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS }), day)
    );
  }

  /**
   * Check if month can be selected
   * @param month selected month
   */
  private isAvailableMonth(month: Date): boolean {
    let disabledMinDate = startOfYear(subYears(this.currentDate, this.multiYear));

    if (this.disabledMinDate) {
      const parsedDate = this.parseDisabledMinDate();
      disabledMinDate = toDate(subMonths(parsedDate, 1));
    }

    let disabledMaxDate = endOfYear(addYears(this.currentDate, this.multiYear));
    if (this.disabledMaxDate) {
      disabledMaxDate = toDate(this.parseDisabledMaxDate());
    }

    if ((this.disabledMinDate && isSameDay(disabledMinDate, month)) || isAfter(disabledMinDate, month) || isBefore(disabledMaxDate, month)) {
      return false;
    }

    return true;
  }

  /**
   * Check if day in calendar is today and highlight it
   * @param day
   */
  private isToday(day: DayDetails): boolean {
    if (isSameDay(this.currentDate, day.value ?? '')) {
      return true;
    }

    return false;
  }

  /*
   * Only today or active date is tabbed similar to MUI
   */
  private setTabIndex = (day: DayDetails) => {
    if (!this.setActiveDate && this.isToday(day)) {
      return 0;
    }
    if (this.isActive(day)) {
      this._catIsToday?.setAttribute('tabindex', '-1');
      return 0;
    }
    return -1;
  };

  private focusOnMonth = (month: HTMLElement) => {
    if (!month) {
      return;
    }

    month?.focus();
    this.selectedMonth = month;
    month.classList.add('cat-c-month-selector__button-focus-active');
  };

  private removeFocusOnBlur(event: FocusEvent) {
    (event.currentTarget as HTMLElement).classList.remove('cat-c-month-selector__button-focus-active');
  }

  /*
   * Get the next month when month dropdown is active
   * 1) Get the next sibling of the current month selection and focus on that.
   * 2) If the no next item siblings then move to next row for the month selection
   */
  private getNextMonth = () => {
    let nextElement = this.selectedMonth?.parentElement?.nextElementSibling;
    let nextMonth = nextElement?.querySelector<HTMLButtonElement>(this.monthSelectorButtonClassName);
    if (nextMonth && !nextMonth.disabled) {
      this.focusOnMonth(nextMonth);
      return;
    }
    nextElement = this.selectedMonth?.closest('[id^="cat-year-"]')?.nextElementSibling;
    nextMonth = nextElement?.querySelector<HTMLButtonElement>(this.monthSelectorButtonClassName);
    if (nextMonth && !nextMonth.disabled) {
      this.focusOnMonth(nextMonth);
    }
  };

  /**
   * Move to lower month when press on down arrow
   * 1. Based on current month selection get the current selection index
   * 2. If the month is available in same year, focus on that below row
   * 3. If the previous month is available in the previous year, focus on that
   */
  private getBelowMonth = () => {
    /* 1 */
    const subListItems = Array.from(
      this.selectedMonth?.closest('.cat-c-month-selector__sub-list')?.querySelectorAll('.cat-c-month-selector__sub-item') || []
    );
    this.focusedMonthIndex = subListItems?.findIndex(
      (subListItem) => subListItem.querySelector('.cat-c-month-selector__button') === this.selectedMonth
    );
    /* 2 */
    if (this.focusedMonthIndex <= 6) {
      const nextMonth = subListItems[this.focusedMonthIndex + 6]?.querySelector<HTMLButtonElement>('.cat-c-month-selector__button');
      if (nextMonth && !nextMonth.disabled) {
        this.focusOnMonth(nextMonth);
        return;
      }
    }
    /* 3 */
    if (this.focusedMonthIndex >= 6) {
      const nextItems = Array.from(
        this.selectedMonth?.closest('[id^="cat-year-"]')?.nextElementSibling?.querySelectorAll('.cat-c-month-selector__sub-item') || []
      );
      this.focusedMonthIndex = this.focusedMonthIndex - 6;
      const nextMonth = nextItems[this.focusedMonthIndex]?.querySelector<HTMLButtonElement>('.cat-c-month-selector__button');
      if (nextMonth && !nextMonth.disabled) {
        this.focusOnMonth(nextMonth);
      }
    }
  };

  /**
   * Move to upper month when press on up arrow
   * 1. Based on current month selection get the current selection index
   * 2. If the month is available in same year, focus on that upper row
   * 3. If the previous month is available in the previous year, focus on that
   */
  private getUpperMonth = () => {
    /* 1*/
    const subListItems = Array.from(
      this.selectedMonth?.closest('.cat-c-month-selector__sub-list')?.querySelectorAll<HTMLElement>('.cat-c-month-selector__sub-item') || []
    );
    this.focusedMonthIndex = subListItems?.findIndex(
      (subListItem: HTMLElement) => subListItem.querySelector('.cat-c-month-selector__button') === this.selectedMonth
    );
    /* 2 */
    if (this.focusedMonthIndex >= 6) {
      const nextMonth = subListItems[this.focusedMonthIndex - 6]?.querySelector<HTMLButtonElement>('.cat-c-month-selector__button');
      if (nextMonth && !nextMonth.disabled) {
        this.focusOnMonth(nextMonth);
      }
    }
    /* 3 */
    if (this.focusedMonthIndex <= 6) {
      const subListItems = Array.from(
        this.selectedMonth?.closest('[id^="cat-year-"]')?.previousElementSibling?.querySelectorAll<HTMLElement>('.cat-c-month-selector__sub-item') ||
          []
      );
      this.focusedMonthIndex = this.focusedMonthIndex + 6;
      const nextMonth = subListItems[this.focusedMonthIndex]?.querySelector<HTMLButtonElement>('.cat-c-month-selector__button');
      if (nextMonth && !nextMonth.disabled) {
        this.focusOnMonth(nextMonth);
        return;
      }
    }
  };

  /**
   * Set the focussed moth index
   */
  private setFocussedMonthIndex = () => {
    const subListItems = Array.from(
      this.selectedMonth?.closest('.cat-c-month-selector__sub-list')?.querySelectorAll<HTMLElement>('.cat-c-month-selector__sub-item') || []
    );
    this.focusedMonthIndex = subListItems?.findIndex((listItem) => listItem.querySelector('.cat-c-month-selector__button') === this.selectedMonth);
  };

  /*
   * Get the previous month
   */
  private getPreviousMonth = () => {
    let prevElement = this.selectedMonth?.parentElement?.previousElementSibling;
    let prevMonth = prevElement?.querySelector<HTMLButtonElement>(this.monthSelectorButtonClassName);
    if (prevMonth && !prevMonth.disabled) {
      this.focusOnMonth(prevMonth);
      return;
    }
    prevElement = this.selectedMonth?.closest('[id^="cat-year-"]')?.previousElementSibling;
    prevMonth = Array.from(prevElement?.querySelectorAll<HTMLButtonElement>(this.monthSelectorButtonClassName) || []).pop();
    if (prevMonth && !prevMonth.disabled) {
      this.focusOnMonth(prevMonth);
    }
  };

  /*
   * Focus first item when any of arrow key pressed
   */
  public focusFirstItem = (e?: KeyboardEvent) => {
    const firstItem = this._catIsActive || this._catIsToday;
    if (!e && firstItem) {
      this.setFocusOnDate(firstItem);
      return;
    }

    const arrowKeys = [KEYS.DOWN_ARROW, KEYS.UP_ARROW, KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW];
    if (e && !this.showMonthPopup && arrowKeys.includes(e.key)) {
      const hasFocussedItem = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-calendar__table-body :focus');

      if (!hasFocussedItem && firstItem) {
        this.setFocusOnDate(firstItem);
      }
      e.preventDefault();
    }
  };

  /*
   * Focus first item when any of arrow key pressed
   */
  public focusOnFirstKeypress = (e: KeyboardEvent) => {
    if (this.focusedDate || this.showMonthPopup) {
      e.preventDefault();
      return;
    }

    const firstItem = this._catIsActive || this._catIsToday;
    if (!this.focusedDate || this._catIsActive) {
      this.focusedDate = firstItem;
      this.focusedDate?.setAttribute('tabindex', '0');
      firstItem?.focus();
      this.getDatePosition();
      return firstItem;
    }
  };

  /*
   * Reset the current focussed date
   */
  private resetCurrentFocussedDate = () => {
    this.focusedDate?.setAttribute('tabindex', '-1');
    this.focusedDate = null;
    this.focusedDateIndex = 0;
  };

  /*
   * Get the date to focus within the month
   */
  private getFocusDateWithInMonth = (currentFocusedDate?: HTMLButtonElement) => {
    let nextFocusDate;
    let nextDate = currentFocusedDate?.closest(this.dateRowSelectorClassName)?.nextElementSibling;
    while (nextDate) {
      nextFocusDate = nextDate?.querySelector<HTMLButtonElement>(this.dateSelectorIsAvailableButtonClassName);
      if (nextFocusDate) {
        break;
      }
      nextDate = nextDate?.closest(this.dateRowSelectorClassName)?.nextElementSibling;
    }
    return nextFocusDate;
  };

  /*
   * Get the date to focus within the month
   */
  private getFocusDatePrevWithInMonth = (currentFocusedDate?: HTMLButtonElement) => {
    let prevFocusDate;
    let prevDate = currentFocusedDate?.closest(this.dateRowSelectorClassName)?.previousElementSibling;
    while (prevDate) {
      prevFocusDate = Array.from(prevDate?.querySelectorAll<HTMLButtonElement>(this.dateSelectorIsAvailableButtonClassName) || []).pop();
      if (prevFocusDate) {
        break;
      }
      prevDate = prevDate.closest(this.dateRowSelectorClassName)?.previousElementSibling;
    }
    return prevFocusDate;
  };

  /*
   * Get the next element to focus when right arrow key pressed
   * 1) Set the current focused date
   * 2) Get the next siblings date and if it is available then focus on it.
   * 3) If no immediate siblings found then search next siblings from current month
   * 4) If no date found in this month then move to next month and repeat the above process
   */
  private focusRightdate = (focusdedDate?: HTMLButtonElement) => {
    let nextFocusDateWithInMonth;
    /* 1 */
    const currentFocusedDate: HTMLButtonElement | null | undefined = focusdedDate || this.focusedDate;
    if (!currentFocusedDate) {
      if (this._catIsToday) {
        this.setFocusOnDate(this._catIsToday);
        return;
      }
      if (this._catIsAvailableAll && this._catIsAvailableAll[0]) {
        this.setFocusOnDate(this._catIsAvailableAll[0]);
        return;
      }
    }
    /* 2 */
    let nextFocusDate: HTMLButtonElement | null = this.findNextEnabledDate(currentFocusedDate);
    if (nextFocusDate) {
      this.setFocusOnDate(nextFocusDate);
      return;
    }
    /* 3 */
    if (currentFocusedDate) {
      nextFocusDateWithInMonth = this.getFocusDateWithInMonth(currentFocusedDate);
      if (nextFocusDateWithInMonth) {
        this.setFocusOnDate(nextFocusDateWithInMonth);
        return;
      }
    }

    if (!this.canChangeAddNavMonth()) {
      return;
    }
    /* 4 */
    this.addNavMonth();
    // settimeout is required due the movement to next month
    setTimeout(() => {
      if (this._catIsAvailableAll && this._catCalendarItems) {
        const nextFocusDate = this._catIsAvailableAll[0];
        if (nextFocusDate) {
          this.setFocusOnDate(nextFocusDate);
        } else {
          this.focusRightdate(this._catCalendarItems[0]);
        }
      }
    }, 50);
  };

  /*
   * Get the next element to focus when right arrow key pressed
   * 1) Get the previous siblings date and if it is available then focus on it.
   * 2) If no immediate siblings found then search previous siblings from current month
   * 3) If no date found in this month then move to previous month and repeat the above process
   */
  private focusLeftdate = (focusdedDate?: HTMLButtonElement) => {
    let prevFocusDateWithInMonth;
    const currentFocusedDate: HTMLButtonElement | null | undefined = focusdedDate || this.focusedDate;
    if (!currentFocusedDate) {
      if (this._catIsToday) {
        this.setFocusOnDate(this._catIsToday);
        return;
      }
      if (this._catIsAvailableAll && this._catIsAvailableAll[0]) {
        this.setFocusOnDate(this._catIsAvailableAll[0]);
        return;
      }
    }
    /* 1 */
    let prevFocusDate = this.findPreviousEnabledDate(currentFocusedDate);
    if (prevFocusDate) {
      this.setFocusOnDate(prevFocusDate);
      return;
    }
    /* 2 */
    if (currentFocusedDate) {
      prevFocusDateWithInMonth = this.getFocusDatePrevWithInMonth(currentFocusedDate);
      if (prevFocusDateWithInMonth) {
        this.setFocusOnDate(prevFocusDateWithInMonth);
        return;
      }
    }
    if (!this.canChangeSubNavMonth()) {
      return;
    }
    /* 3 */
    this.subNavMonth();
    // settimeout is required due the movement to previous month
    setTimeout(() => {
      if (this._catCalendarItems) {
        let nextFocusDate = Array.from(this._catCalendarItems || []).pop();
        /* If the last date is disabled then find the previous enabled date within the month */
        nextFocusDate = nextFocusDate?.disabled ? this.getFocusDatePrevWithInMonth(nextFocusDate) : nextFocusDate;
        if (nextFocusDate) {
          this.setFocusOnDate(nextFocusDate);
        } else {
          this.focusRightdate(this._catCalendarItems[0]);
        }
      }
    }, 50);
  };

  /*
   * Focus down row date when down arrow pressed
   * 1) If no element to select then focus on the first available date
   * 2) Get the next date from the next row same location
   * 3) If no next item move to next month and find the available date
   */
  private focusDownDate = (focusdedDate?: HTMLButtonElement | null, position?: number) => {
    let currentDate = focusdedDate || this.focusedDate;
    /* 1 */
    if (!currentDate) {
      if (this._catIsToday) {
        this.setFocusOnDate(this._catIsToday);
        return;
      }
      if (this._catIsAvailableAll && this._catIsAvailableAll[0]) {
        this.setFocusOnDate(this._catIsAvailableAll[0]);
        return;
      }
    }
    if (!currentDate) {
      return;
    }
    /* 2 */
    let nextFocusDate = this.retrieveNextDateButton(position, currentDate);
    if (nextFocusDate) {
      this.setFocusOnDate(nextFocusDate);
      return;
    }
    if (!this.canChangeAddNavMonth()) {
      return;
    }
    /* 3 */
    this.addNavMonth();
    // settimeout is required due the movement to next month
    setTimeout(() => {
      if (this._catIsAvailableAll && position && this._catCalendarItems) {
        const nextDateRows = Array.from(this._catIsAvailableAll[0]?.closest(this.dateRowSelectorClassName)?.children || []);
        const nextDate = nextDateRows[position]?.querySelector<HTMLButtonElement>(this.dateSelectorIsAvailableButtonClassName);
        if (nextDate) {
          this.setFocusOnDate(nextDate);
        } else {
          this.focusDownDate(this._catCalendarItems[0], position);
        }
      }
    }, 50);
  };

  /*
   * Retrieve the next date button from the next row based on the current focused date position
   * @returns next enabled date button element
   */
  private retrieveNextDateButton(position: number | undefined, currentDate: HTMLButtonElement) {
    this.focusedDateIndex = position || this.focusedDateIndex;
    const nextDateRows = Array.from(currentDate?.closest(this.dateRowSelectorClassName)?.nextElementSibling?.children || []);
    let nextFocusDate = nextDateRows[this.focusedDateIndex]?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    nextFocusDate = this.getDownEnabledNextDate(nextFocusDate) ?? null;
    return nextFocusDate;
  }

  /*
   * Find the previous enabled date button element
   * 1) Get the previous siblings date and if it is available then focus on it.
   * 2) If no immediate siblings found then search previous siblings from current month
   * @returns previous enabled date button element
   */
  private findPreviousEnabledDate(currentFocusedDate: HTMLButtonElement | null | undefined) {
    let prevFocusDate = currentFocusedDate?.parentElement?.previousElementSibling?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    prevFocusDate = this.getEnabledPreviousDate(prevFocusDate);
    return prevFocusDate;
  }

  /*
   * Find the next enabled date button element
   * 1) Get the next siblings date and if it is available then focus on it.
   * 2) If no immediate siblings found then search next siblings from current month
   * @returns next enabled date button element
   */
  private findNextEnabledDate(currentFocusedDate: HTMLButtonElement | null | undefined) {
    let nextFocusDate: HTMLButtonElement | null =
      currentFocusedDate?.parentElement?.nextElementSibling?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName) ?? null;
    nextFocusDate = this.getEnabledNextDate(nextFocusDate) ?? null;
    return nextFocusDate;
  }

  /*
   * Get the next enabled date in the down row
   * 1) If the next date is disabled then find the next enabled date in the same row
   * 2) If no enabled date found in the same row then find the next enabled date in the next rows
   *   @returns next enabled date button element
   */
  private getDownEnabledNextDate(nextFocusDate: HTMLButtonElement | null | undefined) {
    while (nextFocusDate && nextFocusDate.disabled) {
      const nextDateRows = Array.from(nextFocusDate?.closest(this.dateRowSelectorClassName)?.nextElementSibling?.children || []);
      nextFocusDate = nextDateRows[this.focusedDateIndex]?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    }
    return nextFocusDate;
  }

  /*
   * Focus up row date when up arrow pressed
   * 1) Get the previous date from the previous row same location
   * 2) If no item then move to previous month and find the available date and focus
   */
  private focusUpDate = (focusdedDate?: HTMLButtonElement | null, position?: number) => {
    const element = focusdedDate || this.focusedDate;
    this.focusedDateIndex = position || this.focusedDateIndex;
    if (!element) {
      if (this._catIsToday) {
        this.setFocusOnDate(this._catIsToday);
        return;
      }
      if (this._catIsAvailableAll && this._catIsAvailableAll[0]) {
        this.setFocusOnDate(this._catIsAvailableAll[0]);
        return;
      }
    }
    /* 1 */
    let prevFocusDate = this.retrievePrevEnabledDate(element);
    if (prevFocusDate) {
      this.setFocusOnDate(prevFocusDate);
      return;
    }
    if (!this.canChangeSubNavMonth()) {
      return;
    }
    /* 2 */
    this.subNavMonth();
    // settimeout is required due the movement to previous month
    setTimeout(() => {
      if (this._catIsAvailableAll && position) {
        const lastDateFromNextMonth = Array.from(this._catIsAvailableAll).pop();
        const prevMonthRowDates = Array.from(lastDateFromNextMonth?.closest(this.dateRowSelectorClassName)?.children || []);
        const prevDate = prevMonthRowDates[position]?.querySelector<HTMLButtonElement>(this.dateSelectorIsAvailableButtonClassName);
        if (prevDate) {
          this.setFocusOnDate(prevDate);
        } else {
          this.focusUpDate(Array.from(this._catCalendarItems || []).pop(), position);
        }
      }
    }, 50);
  };

  /*
   * Retrieve the previous enabled date button from the previous row based on the current focused date position
   * @returns previous enabled date button element
   */
  private retrievePrevEnabledDate(element: HTMLButtonElement | null | undefined) {
    const prevDateRows = Array.from(element?.closest(this.dateRowSelectorClassName)?.previousElementSibling?.children || []);
    let prevFocusDate = prevDateRows[this.focusedDateIndex]?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    prevFocusDate = this.getUpEnabledPreviousDate(prevFocusDate) ?? null;
    return prevFocusDate;
  }

  private getUpEnabledPreviousDate(prevFocusDate: HTMLButtonElement | null | undefined) {
    while (prevFocusDate && prevFocusDate.disabled) {
      const nextDateRows = Array.from(prevFocusDate?.closest(this.dateRowSelectorClassName)?.previousElementSibling?.children || []);
      prevFocusDate = nextDateRows[this.focusedDateIndex]?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    }
    return prevFocusDate;
  }

  /**
   * Get the previous enabled date
   * 1) If the previous date is disabled then find the previous enabled date
   * @returns previous enabled date button element
   */
  private getEnabledPreviousDate(prevFocusDate: HTMLButtonElement | null | undefined) {
    while (prevFocusDate && prevFocusDate.disabled) {
      prevFocusDate = prevFocusDate?.parentElement?.previousElementSibling?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    }
    return prevFocusDate;
  }

  /**
   * Get the next enabled date
   * 1) If the next date is disabled then find the next enabled date
   * @returns next enabled date button element
   */
  private getEnabledNextDate(nextFocusDate: HTMLButtonElement | null | undefined) {
    while (nextFocusDate && nextFocusDate.disabled) {
      nextFocusDate = nextFocusDate?.parentElement?.nextElementSibling?.querySelector<HTMLButtonElement>(this.dateSelectorButtonClassName);
    }
    return nextFocusDate;
  }

  /**
   * Set todays date as focused
   */
  private setTodaysDate(e: KeyboardEvent) {
    const target = e.target as Element;
    const todaysClassList = ['is-available', 'is-today'];

    if (this.focusedDate) {
      return;
    }

    const isTodayDateAvailable = todaysClassList.every((item) => Array.from(target.classList || []).includes(item));
    if (isTodayDateAvailable && this._catIsToday) {
      this.setFocusOnDate(this._catIsToday, false);
    }
  }

  /*
   * Handle key press
   */
  private handleDateKeypress = (e: KeyboardEvent) => {
    if (e.key === KEYS.TAB) {
      this.setTodaysDate(e);
    }
    if (this.showMonthPopup) {
      return;
    }
    if (e.key === KEYS.RIGHT_ARROW) {
      this.setTodaysDate(e);
      this.focusRightdate();
    }

    if (e.key === KEYS.LEFT_ARROW) {
      this.setTodaysDate(e);
      this.focusLeftdate();
    }

    if (e.key === KEYS.DOWN_ARROW) {
      this.setTodaysDate(e);
      this.focusDownDate(null, this.focusedDateIndex);
    }

    if (e.key === KEYS.UP_ARROW) {
      this.setTodaysDate(e);
      this.focusUpDate(null, this.focusedDateIndex);
    }
  };

  /*
   * Get the date position based on the element
   */
  private getDatePosition(BtnElement?: HTMLButtonElement) {
    let datePosition = 0;
    const element = BtnElement || this.focusedDate;
    if (element) {
      if (!this.isVisible(element)) {
        return;
      }
      const closestElementChildren = element.closest(this.dateRowSelectorClassName)?.children;
      if (closestElementChildren) {
        const dates = Array.from(closestElementChildren);
        for (const [i, date] of dates.entries()) {
          if (date === element?.parentElement) {
            datePosition = i;
            break;
          }
        }
      }
    }

    this.focusedDateIndex = datePosition;
    if (BtnElement) {
      return datePosition;
    }
  }

  /*
   * Hide the popup
   * Before hiding focus on today else clear the previous focus selection
   */
  public hidePopup = () => {
    this.showMonthPopup = false;
    this.focusedDate?.setAttribute('tabindex', '-1');
    if (this.selectedDate && this._catIsActive) {
      this.setFocusOnDate(this._catIsActive);
      return;
    }
    if (!this.selectedDate) {
      setTimeout(() => {
        this._catIsToday?.setAttribute('tabindex', '0');
      }, 100);
    }
  };

  /*
   * Focus on the date
   * 1) Clear the existing tab index values
   * 2) Set the tabindex only on today or active element
   */
  public setFocusOnDate = (element: HTMLButtonElement, isFocus = true) => {
    if (!element) {
      return;
    }

    this.focusedDate?.setAttribute('tabindex', '-1');
    this._catIsToday?.setAttribute('tabindex', '-1');
    this.focusedDate = element;
    this.focusedDate?.setAttribute('tabindex', '0');

    isFocus && element?.focus();

    this.getDatePosition();
  };

  /**
   * Parses the `disabledMinDate` property into a Date object using the specified date format and time zone.
   *
   * @returns {Date} The parsed date object.
   *
   * - If `disabledMinDate` is not provided, an empty string is used as the default value.
   * - The parsing is performed using the `dateFormat` and the converted date based on the `timeZone`.
   * - The parsing process also applies the `enUS` locale for date interpretation.
   */
  private parseDisabledMinDate() {
    return parse(this.disabledMinDate ?? '', this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });
  }

  /**
   * Parses the `disabledMaxDate` property into a Date object using the specified date format and time zone.
   *
   * @returns {Date} The parsed `disabledMaxDate` as a Date object.
   * If `disabledMaxDate` is not provided, an empty string is used as the default value.
   *
   * @remarks
   * - The parsing is performed using the `parse` function, which takes into account the provided `dateFormat`.
   * - The `getConvertedDate` function is used to adjust the date based on the specified `timeZone`.
   * - The `enUS` locale is applied during parsing.
   */
  private parseDisabledMaxDate() {
    return parse(this.disabledMaxDate ?? '', this.dateFormat, getConvertedDate(this.timeZone), { locale: enUS });
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-calendar', {
      'cat-c-calendar-month-panel--open': this.showMonthPopup
    });

    return html`
      <div part="base" class="${componentClassNames}" @keydown=${this.handleDateKeypress}>
        <nav part="header" class="cat-c-calendar__header">
          <cat-button
          part="previous-nav" 
          exportparts=${this.getExportParts('button-previous-nav', 'button')}
            type="button"
            variant="ghost"
            class="cat-c-calendar__nav-btn"
            @click=${() => this.subNavMonth()}
            ?hideText=${true}
            ?disabled="${!this.canChangeSubNavMonth()}"
          >
          ${this.previousButtonText}
          <slot part="part-before" slot="before">
            <cat-icon-arrow-triangular-left part="triangular-left-icon" exportparts=${this.getExportParts(
              'triangular-left',
              'icon'
            )}></cat-icon-arrow-triangular-left>
          </slot>
          </cat-button>
          <button part="month-button" class="cat-c-calendar__month-selector-button" data-year="${format(this.navDate ?? '', 'y')}" id="${ifDefined(
            this.datepickerId
          )}"
          aria-live="polite" @click=${() => this.toggleMonthPopup()}>
            ${format(this.navDate ?? '', 'MMMM yyyy')}
            <cat-icon-chevron-down part="chevron-down-icon" exportparts=${this.getExportParts(
              'chevron-down',
              'icon'
            )} class="cat-c-calendar__month-selector-button-icon"></cat-icon-chevron-down>
          </button>
          <cat-button
          part="next-nav" 
          exportparts=${this.getExportParts('button-next-nav', 'button')}
            type="button"
            variant="ghost"
            class="cat-c-calendar__nav-btn"
            @click=${() => this.addNavMonth()}
            ?hideText=${true}
            ?disabled="${!this.canChangeAddNavMonth()}"
          >
          ${this.nextButtonText}
          <slot part="part-after" slot="after">
            <cat-icon-arrow-triangular-right part="triangular-right-icon" exportparts=${this.getExportParts(
              'arrow-triangular-right',
              'icon'
            )}></cat-icon-arrow-triangular-right>
          </slot>
          </cat-button>
        </nav>

        <div part="table-container" class="cat-c-calendar__table-container">
          <table part="table" role="presentation" class="cat-c-calendar__table">
            <thead part="table-header" class="cat-c-calendar__table-header">
              <tr part="week-table-row" class="cat-c-calendar__table-row">
                ${this.weekDaysHeader().map((day) => {
                  return html` <th part="header-cell" abbr="${day}" class="cat-c-calendar__header-cell">${day}</th> `;
                })}
              </tr>
            </thead>
            <tbody part="table-body" class="cat-c-calendar__table-body">
              ${this.days.map((week: DayDetails[]) => {
                return html`
                  <tr part="day-table-row" class="cat-c-calendar__table-row">
                    ${week.map((day: DayDetails) => {
                      return html`
                        <td part="table-cell" class="cat-c-calendar__table-cell">
                          ${day.value !== null
                            ? html`
                                <button
                                  part="day"
                                  type="button"
                                  class="cat-c-calendar__item
                                  ${this.isActive(day) ? 'is-active' : ''}
                                  ${this.isAvailableDay(day.value) ? 'is-available' : ''}
                                  ${this.isToday(day) ? 'is-today' : ''}"
                                  aria-pressed=${ifDefined(this.isActive(day) ? true : undefined)}
                                  @click=${() => this.selectDay(day)}
                                  ?disabled=${!this.isAvailableDay(day.value)}
                                  aria-label="${format(day.value, this.fullDateFormat)}"
                                  tabindex=${this.setTabIndex(day)}
                                >
                                  ${format(day.value, 'd')}
                                </button>
                              `
                            : ''}
                        </td>
                      `;
                    })}
                  </tr>
                `;
              })}
            </tbody>
          </table>
        </div>

        <div part="month-popup" class="cat-c-calendar__month-selector-popup" aria-label=${ifDefined(this.arialabel)} role="dialog" ?hidden="${!this.showMonthPopup}" tabindex="0" @keydown=${
          this.handleKeyDown
        }>
          <div part="month-selector" class="cat-c-month-selector">
            <ul part="month-list" class="cat-c-month-selector__list">
              ${this.years.map((year) => {
                return html`
                  <li part="month-selector_item" class="cat-c-month-selector__item" id="cat-year-${format(year.value, 'y')}">
                    <h4 part="month-selector-year" class="cat-c-month-selector__year-heading">${format(year.value, 'y')}</h4>
                    <ul part="month-selector_sub-list" class="cat-c-month-selector__sub-list">
                      ${year.month
                        ? year?.month.map((month: Date) => {
                            return html`
                              <li part="month-selector_sub-item" class="cat-c-month-selector__sub-item">
                                <button
                                  part="month-selector_button"
                                  class="cat-c-month-selector__button
                              ${isSameMonth(month, this.currentDate) ? 'is-current' : ''}
                              ${isEqual(startOfMonth(startOfDay(this.navDate ?? '')), startOfMonth(startOfDay(month))) ? 'is-active' : ''}"
                                  ?disabled=${!this.isAvailableMonth(month)}
                                  @click=${() => {
                                    this.updateGrid(month);
                                    this.focusMonthButton();
                                  }}
                                  @blur=${this.removeFocusOnBlur}
                                  aria-label="${format(month, 'MMMM, y')}"
                                  tabindex="-1"
                                >
                                  <abbr part="abbr" title="${format(month, 'MMMM')}">${format(month, 'MMM')}</abbr>
                                </button>
                              </li>
                            `;
                          })
                        : html`${nothing}`}
                    </ul>
                  </li>
                `;
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
    `;
  }
}

if (customElements.get('cat-calendar') === undefined) {
  customElements.define('cat-calendar', BlocksCalendar);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-calendar': BlocksCalendar;
  }
}
